import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, PolarRadiusAxis } from "recharts";
import { ArrowRight } from "lucide-react";
import useSeo from "@/lib/useSeo";

export default function Benchmark() {
  const [data, setData] = useState(null);

  useSeo({
    title: "The Value Realisation Index — Live Benchmark | Langwood360",
    description: "The anonymised, continuously-updated median Value Realisation Index across every board that has completed the diagnostic.",
    path: "/benchmark",
  });

  useEffect(() => {
    api.get("/benchmark").then(({ data }) => setData(data)).catch(() => setData({ ready: false, sample_size: 0 }));
  }, []);

  const ready = data?.ready;

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="tick-label-accent mb-6">The Live Benchmark</div>
          <h1 className="display-serif text-5xl md:text-8xl leading-[0.95]">
            The industry median<br/>
            <em className="italic text-orange">right now</em>.
          </h1>
          <div className="rule-orange my-10" />
          <p className="max-w-2xl text-lg text-charcoal leading-relaxed">
            The Value Realisation Index is a live diagnostic. Every board that
            completes it strengthens the median below. What follows is the
            anonymised, continuously-updated view of where organisations stand
            — a trailer for the annual research publication.
          </p>
        </motion.div>
      </section>

      {!ready && (
        <section className="max-w-4xl mx-auto px-6 lg:px-12 py-16" data-testid="benchmark-early">
          <div className="border border-oxford/15 bg-linen p-10">
            <div className="tick-label-accent mb-3">Early cohort</div>
            <h2 className="display-serif text-3xl">The median is coming.</h2>
            <p className="mt-4 text-charcoal max-w-lg">
              {data ? `Only ${data.sample_size} boards have completed the Index so far. The median goes live at five submissions.` : "Loading…"}
            </p>
            <Link to="/diagnostic" className="btn-orange mt-8">
              Take the Index <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </Link>
          </div>
        </section>
      )}

      {ready && (
        <>
          <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-oxford/10">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                className="bg-linen p-10 lg:col-span-2" data-testid="benchmark-median">
                <div className="tick-label">Global Median VRI</div>
                <div className="mt-6 flex items-baseline gap-6">
                  <div className="font-mono text-[9rem] leading-none text-oxford">{data.median_vri}</div>
                  <div className="text-oxford/60 text-3xl">/ 125</div>
                </div>
                <div className="mt-8 text-charcoal">
                  Sample size: <span className="text-oxford font-mono">{data.sample_size}</span> completed diagnostics.
                  Middle 50% sit between <span className="text-oxford font-mono">{data.p25_vri}</span> and <span className="text-oxford font-mono">{data.p75_vri}</span>.
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
                className="bg-linen p-8" data-testid="benchmark-radar">
                <div className="tick-label-accent mb-3">Dimension medians</div>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={data.dimension_medians.map(d => ({ dim: d.dimension, score: d.score }))} outerRadius="70%">
                    <PolarGrid gridType="polygon" stroke="#0E2148" strokeOpacity={0.15} />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: "#0E2148", fontSize: 10 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#6D82B4", fontSize: 9 }} axisLine={false} />
                    <Radar dataKey="score" stroke="#E86A20" fill="#E86A20" fillOpacity={0.22} strokeWidth={1.8} />
                  </RadarChart>
                </ResponsiveContainer>
              </motion.div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
            <div className="tick-label-accent mb-6">Where the median stands</div>
            <div className="space-y-4">
              {data.dimension_medians.map((d, i) => (
                <motion.div key={d.dimension}
                  initial={{ opacity: 0, x: -14 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="flex items-center gap-6"
                >
                  <div className="w-48 display-serif text-xl">{d.dimension}</div>
                  <div className="flex-1 h-px bg-oxford/10 relative">
                    <motion.div
                      initial={{ width: 0 }} whileInView={{ width: `${d.score}%` }} viewport={{ once: true }}
                      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.06 }}
                      className="absolute top-0 left-0 h-px bg-orange"
                    />
                  </div>
                  <div className="w-16 font-mono text-lg text-oxford text-right">{d.score}%</div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Certificate Wall */}
          {data.recent_leaders && data.recent_leaders.length > 0 && (
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="certificate-wall">
              <div className="tick-label-accent mb-6">Recent Leaders</div>
              <div className="display-serif text-3xl md:text-5xl mb-10">Organisations who earned the certificate.</div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-oxford/10">
                {data.recent_leaders.map((l, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="bg-linen p-6"
                    data-testid={`leader-card-${i}`}
                  >
                    <div className="tick-label-accent">Value Realisation Leader</div>
                    <div className="display-serif text-xl mt-3 leading-tight">{l.organisation}</div>
                    <div className="mt-4 flex items-baseline gap-2">
                      <div className="font-mono text-3xl text-oxford">{l.vri}</div>
                      <div className="text-charcoal text-sm">/ 125</div>
                    </div>
                    <div className="tick-label mt-3">{(l.awarded_at || "").slice(0, 10)}</div>
                  </motion.div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="border-t border-oxford/10 pt-10 flex flex-wrap items-baseline justify-between gap-6">
          <div>
            <div className="tick-label-accent mb-2">Contribute to the research</div>
            <h3 className="display-serif text-3xl md:text-4xl max-w-2xl">Your VRI strengthens the median.</h3>
          </div>
          <Link to="/diagnostic" data-testid="benchmark-cta" className="btn-orange">
            Take the Index <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
